import React, { useEffect, useMemo, useState } from "react";
import TabTitle from "../components/TabTitle";
import CustomBreadcrumb from "../components/CustomBreadcrumb";
import { useDispatch, useSelector } from "react-redux";
import { fetchAttempts } from "../store/attemptsSlice";
import moment from "moment";
import { Button, Input, Tooltip } from "antd";
import {
  CheckSquareOutlined,
  CloseSquareOutlined,
  DownloadOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { fetchAssessmentsExcel } from "../store/assessmentsSlice";
import DynamicTable from "../components/CustomTable";

const Attempts = () => {
  const dispatch = useDispatch();
  const [attemptsDisplayed, setAttemptsDisplayed] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(15);
  const [page, setPage] = useState(1);
  const { data, loading, error } = useSelector((state) => state.attempts);
  const { loading: assessmentsLoading } = useSelector(
    (state) => state.assessments
  );
  const user = useSelector((state) => state.users.singleRecord?.data);

  const companyId = user?.company;

   const queryParams = useMemo(() => ({
     search: searchQuery,
     page,
     page_size: perPage,
     ...(companyId && { company_id: companyId }),
   }), [searchQuery, page, perPage, companyId]);
 
   useEffect(() => {
    dispatch(fetchAttempts(queryParams));
   }, [dispatch, queryParams]);
 
  useEffect(() => {
    if (data && data?.page_details?.count)
      setTotalRows(data?.page_details?.count);
    if (data && !loading && data?.data?.length > 0) {
      setAttemptsDisplayed(data?.data);
    } else setAttemptsDisplayed([]);
  }, [data, loading, error]);

  const breadcrumbList = [{ title: "Attempts", href: "" }];

  const columns = [
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Assessment Name",
      dataIndex: "assessment_name",
      key: "assessment_name",
    },
    {
      title: "Score",
      dataIndex: "final_score",
      key: "final_score",
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => text, // Formatting date if needed
    },
  ];

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value); // Update search query
  };

  const tableData =
    attemptsDisplayed && attemptsDisplayed.length > 0
      ? attemptsDisplayed.map((attempt) => {
          return {
            email: (
              <div>
                {attempt?.status === "Passed" && (
                  <Tooltip title="Passed">
                    <CheckSquareOutlined className="me-1" />
                  </Tooltip>
                )}
                {attempt?.status === "Failed" && (
                  <Tooltip title="Failed">
                    <CloseSquareOutlined
                      className="me-1"
                      style={{ color: "#ff4d4f" }}
                    />
                  </Tooltip>
                )}
                {attempt?.email}
              </div>
            ),
            assessment_name: attempt?.assessment_name,
            final_score: `${attempt?.final_score?.toFixed(2)} (${
              attempt?.correct_responses
            }/${attempt?.total_questions}) %`,
            created_at: moment
              .utc(attempt?.created_at)
              .format("DD MMM YYYY HH:mm"),
          };
        })
      : [];

  const handleRefresh = () => {
    dispatch(fetchAttempts({})); // Dispatch the fetchUsers action to get data
  };

  const handleResultDownload = async () => {
    // Dispatch the async thunk and await the result
    const actionResult = await dispatch(fetchAssessmentsExcel());

    // Log the action result for debugging
    console.log("Action Result:", actionResult);

    // Check if the action was fulfilled
    if (fetchAssessmentsExcel.fulfilled.match(actionResult)) {
      const excelBlob = actionResult.payload; // This should be the Blob containing the Excel data

      // If the payload contains the blob, trigger the download
      if (excelBlob) {
        // Create a URL for the Blob object
        const url = window.URL.createObjectURL(new Blob([excelBlob]));

        // Create a temporary link element to simulate the download action
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "assessment_results.xlsx"); // Set the desired filename
        document.body.appendChild(link);
        link.click(); // Trigger the download

        // Clean up the object URL
        window.URL.revokeObjectURL(url);
      } else {
        console.error("Received empty Blob data");
      }
    } else {
      // Handle failure (rejected action or error)
      console.error("Error downloading the Excel file:", actionResult.payload);
    }
  };

  // Pagination configuration
  const pagination = {
    current: page, // Current page
    pageSize: 15, // Number of items per page
    total: totalRows, // Total number of records (adjust as per your data)
    onChange: (page) => {
      console.log("Page:", page);
      setPage(page);
    },
  };

  return (
    <>
      <TabTitle title="Attempts" />
      <CustomBreadcrumb breadcrumbList={breadcrumbList} />
      <div className="main-content">
        <div className="container-fluid bg-white  ">
          <div className="row mb-4">
            <div className="col-8 col-sm-8 col-md-5 col-lg-5">
              <div className="px-4">
                <Input
                  className="custom_input_antd"
                  size="large"
                  value={searchQuery}
                  placeholder="Search"
                  allowClear
                  onChange={handleSearchChange}
                />
              </div>
            </div>
            <div className="col-4 col-sm-4 col-md-7 col-lg-7">
              <div className="px-4 d-flex justify-content-end align-items-center gap-2">
                <Tooltip title="Download results as Excel">
                  <Button
                    className="custom_icon_btn"
                    onClick={handleResultDownload}
                    loading={assessmentsLoading}
                    type="primary"
                    shape="circle"
                    size="large"
                    icon={<DownloadOutlined />}
                  />
                </Tooltip>
                <Tooltip title="Refresh the data">
                  <Button
                    className="custom_icon_btn"
                    onClick={handleRefresh}
                    loading={loading}
                    type="primary"
                    shape="circle"
                    size="large"
                    icon={<ReloadOutlined />}
                  />
                </Tooltip>
              </div>
            </div>
          </div>
          {/* <div className="row mb-4">
                        <div className="col-lg-12">
                            <div className="customize-table">
                                <CustomDataTable
                                    progressPending={loading}
                                    columns={columns} data={tableData}
                                    pagination paginationServer paginationTotalRows={totalRows} onChangeRowsPerPage={handlePerRowsChange} onChangePage={handlePageChange}
                                />
                            </div>
                        </div>
                    </div> */}
          <DynamicTable
            columns={columns}
            loading={loading} // Show loading spinner when true
            data={tableData}
            onRowClick={(record) => console.log("Row clicked:", record)}
            pagination={pagination} // Pass pagination prop
          />
        </div>
      </div>
    </>
  );
};

export default Attempts;
